import React, { useMemo } from 'react';
import { useSeason } from '../../SeasonContext';
import { EXPOSURE_PCT, computeMaxSpeedSummary } from './maxSpeedShared';

// Einstein compose panel for Max Speed (2026-09-10) — same pattern as MatchChronicPanel.js/
// MedicalPanel.js: presentational only, takes a player id plus the shared reference data (fetched
// once for the whole compose response) and renders through computeMaxSpeedSummary, the exact
// pure function MaxSpeed.js's own individual view now uses too — a panel's numbers can never
// silently drift from the full page's. useSeason() here reads whatever season filter is currently
// selected app-wide, same as the full page.
const fmtSpd = n => (n == null || n <= 0) ? '—' : n.toFixed(1);
const fmtDate = d => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

export default function MaxSpeedPanel({ playerId, players, sessions, mssByPlayer }) {
  const { selectedSeasons } = useSeason();
  const player = useMemo(() => players.find(p => String(p.id) === String(playerId)), [players, playerId]);

  const summary = useMemo(
    () => computeMaxSpeedSummary(playerId, sessions, mssByPlayer, selectedSeasons),
    [playerId, sessions, mssByPlayer, selectedSeasons]
  );
  const { personalMax, seasonMax, lastRecord } = summary;

  if (!player) {
    return <div style={{ padding: 16, color: 'var(--text-dimmed)', fontSize: 13 }}>No player found for id {playerId}.</div>;
  }

  const lastPct = lastRecord && personalMax ? (lastRecord.maxSpeed / personalMax) * 100 : null;
  const exposurePass = lastPct != null ? lastPct >= EXPOSURE_PCT : null;

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', minWidth: 240 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{player.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 14 }}>Max Speed</div>

      <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Personal max</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtSpd(personalMax)} <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>km/h</span></div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Season best</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtSpd(seasonMax)} <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>km/h</span></div>
        </div>
      </div>

      {lastRecord ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Last: <strong>{fmtSpd(lastRecord.maxSpeed)} km/h</strong> on {fmtDate(lastRecord.date)}
          {lastPct != null && (
            <span style={{ color: exposurePass ? '#10b981' : '#ef4444', fontWeight: 600 }}> ({Math.round(lastPct)}% of max)</span>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No max speed data recorded.</div>
      )}
    </div>
  );
}
